/**
 * Document Generation Service
 * 
 * Frontend service for generating PDF documents via the backend API
 */

export interface DocumentGenerationResponse {
  success: boolean;
  pdfUrl?: string;
  filename?: string;
  error?: string;
}

export interface DocumentGenerationRequest {
  type: 'farmer-report' | 'invoice' | 'policy-summary';
  metadata: Record<string, unknown>;
}

export interface DocumentServiceStatus {
  success: boolean;
  data?: {
    overall: {
      available: boolean;
      message: string;
    };
    services: {
      templates: {
        available: boolean;
        message: string;
        templates?: string[];
      };
      processor: {
        available: boolean;
        url: string;
        message?: string;
      };
      storage: {
        available: boolean;
        message: string;
      };
    };
  };
  error?: string;
  reason?: string;
}

/**
 * Generate a PDF report for a farmer
 * 
 * @param farmerId - ID of the farmer to generate report for
 * @returns Promise resolving to document generation response
 */
export async function generateFarmerPdf(farmerId: string): Promise<DocumentGenerationResponse> {
  try {
    console.log(`📄 Generating PDF for farmer: ${farmerId}`);
    
    // Use fetch directly since apiClient doesn't have document methods yet
    const token = localStorage.getItem('auth-token');
    const response = await fetch(`/api/documents/farmer/${farmerId}/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();

    if (result.success && result.data?.pdfUrl) {
      console.log(`✅ PDF generated successfully: ${result.data.filename}`);
      return {
        success: true,
        pdfUrl: result.data.pdfUrl,
        filename: result.data.filename
      };
    } else {
      throw new Error(result.error || 'Failed to generate PDF');
    }
  } catch (error) {
    console.error('❌ PDF generation failed:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to generate farmer PDF'
    );
  }
}

/**
 * Generate a methodology PDF report for a product
 * 
 * @param productId - ID of the product to generate methodology PDF for
 * @returns Promise resolving to document generation response
 */
export async function generateProductMethodologyPdf(productId: string): Promise<DocumentGenerationResponse> {
  try {
    console.log(`📄 Generating methodology PDF for product: ${productId}`);
    
    // Use fetch directly since apiClient doesn't have document methods yet
    const token = localStorage.getItem('auth-token');
    const response = await fetch(`/api/documents/product/${productId}/methodology-pdf`, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();

    if (result.success && result.data?.pdfUrl) {
      console.log(`✅ Methodology PDF generated successfully: ${result.data.filename}`);
      return {
        success: true,
        pdfUrl: result.data.pdfUrl,
        filename: result.data.filename
      };
    } else {
      throw new Error(result.error || 'Failed to generate methodology PDF');
    }
  } catch (error) {
    console.error('❌ Methodology PDF generation failed:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to generate product methodology PDF'
    );
  }
}

/**
 * Generate a document using the generic endpoint
 * 
 * @param request - Document generation request
 * @returns Promise resolving to document generation response
 */
export async function generateDocument(request: DocumentGenerationRequest): Promise<DocumentGenerationResponse> {
  try {
    console.log(`📄 Generating document: ${request.type}`);
    
    // Use fetch directly since apiClient doesn't have document methods yet
    const token = localStorage.getItem('auth-token');
    const response = await fetch('/api/documents/generate', {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();

    if (result.success && result.data?.pdfUrl) {
      console.log(`✅ Document generated successfully: ${result.data.filename}`);
      return {
        success: true,
        pdfUrl: result.data.pdfUrl,
        filename: result.data.filename
      };
    } else {
      throw new Error(result.error || 'Failed to generate document');
    }
  } catch (error) {
    console.error('❌ Document generation failed:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to generate document'
    );
  }
}

/**
 * Open a PDF URL in a new browser window with authentication
 * 
 * @param pdfUrl - URL to the PDF file
 * @param filename - Optional filename for the window title
 */
export async function openPdfInNewWindow(pdfUrl: string, filename?: string): Promise<void> {
  try {
    console.log(`📖 Opening PDF with authentication: ${pdfUrl}`);
    
    // Fetch the PDF with authentication headers
    const token = localStorage.getItem('auth-token');
    const response = await fetch(pdfUrl, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download PDF: ${response.statusText}`);
    }

    // Create blob from response
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    
    // Open in new window
    const windowName = filename ? `pdf_${filename}` : 'pdf_viewer';
    const windowFeatures = 'width=800,height=900,scrollbars=yes,resizable=yes';
    
    const pdfWindow = window.open(blobUrl, windowName, windowFeatures);
    
    if (!pdfWindow) {
      // Fallback: try to download if popup was blocked
      console.warn('⚠️ Popup blocked, attempting to download PDF...');
      await downloadPdf(pdfUrl, filename);
    } else {
      pdfWindow.focus();
      console.log('📖 PDF opened in new window');
      
      // Clean up blob URL after a delay to ensure it loads
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 5000);
    }
  } catch (error) {
    console.error('❌ Failed to open PDF:', error);
    // Fallback to download
    await downloadPdf(pdfUrl, filename);
  }
}

/**
 * Download a PDF file with authentication
 * 
 * @param pdfUrl - URL to the PDF file
 * @param filename - Optional filename for download
 */
export async function downloadPdf(pdfUrl: string, filename?: string): Promise<void> {
  try {
    console.log(`📥 Downloading PDF with authentication: ${pdfUrl}`);
    
    // Fetch the PDF with authentication headers
    const token = localStorage.getItem('auth-token');
    const response = await fetch(pdfUrl, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download PDF: ${response.statusText}`);
    }

    // Create blob from response
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    
    // Create download link
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'document.pdf';
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up blob URL
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 1000);
    
    console.log('📥 PDF download initiated');
  } catch (error) {
    console.error('❌ Failed to download PDF:', error);
    throw error;
  }
}

/**
 * Check document generation service status
 * 
 * @returns Promise resolving to service status
 */
export async function getDocumentServiceStatus(): Promise<DocumentServiceStatus> {
  try {
    // Use fetch directly since apiClient doesn't have document methods yet
    const token = localStorage.getItem('auth-token');
    const response = await fetch('/api/documents/status', {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('❌ Failed to get document service status:', error);
    throw error;
  }
}